import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw"; // ✅ ADDED

import AccessibilityPlannerConfig from "./AccessibilityPlanner.config";
import AccessibilityPlannerSchema from "./AccessibilityPlanner.schema";

import "../../../assets/stylesheets/toolBase.css";

const STORAGE_KEY = "accessibility_planner_state"; // ✅ NEW

const AccessibilityPlannerTool = ({ category }) => {
  const {
    title,
    description,
    sections,
    defaults,
    ctaLabel,
    result,
  } = AccessibilityPlannerConfig;

  const [responses, setResponses] = useState({ ...defaults });
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [streamedReport, setStreamedReport] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // ✅ RESTORE STATE ON LOAD
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        if (parsed?.hasSubmitted && parsed?.streamedReport) {
          setResponses(parsed.responses || defaults);
          setHasSubmitted(true);
          setStreamedReport(parsed.streamedReport);
        }
      } catch (e) {
        console.error("Failed to restore state:", e);
      }
    }

    setIsHydrated(true);
  }, []);

  // ✅ PERSIST STATE
  useEffect(() => {
    if (hasSubmitted) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          responses,
          hasSubmitted,
          streamedReport,
        })
      );
    }
  }, [responses, hasSubmitted, streamedReport]);

  const handleChange = (field, value) => {
    setResponses((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ✅ UPDATED: keyword → project slug mapping
  const recommendationLinks = {
    stairlift: "showers-walk-in-tubs",
    ramp: "showers-walk-in-tubs",
    "walk-in shower": "showers-walk-in-tubs",
    bathtub: "showers-walk-in-tubs",

    "non-slip": "flooring",
    flooring: "flooring",

    doorways: "exterior-door-replacement",
    doors: "exterior-door-replacement",
  };

  const cleanChunk = (text) => {
    return text
      .replace(/##+/g, "\n## ")
      .replace(/\*\*/g, "**")

      .replace(/Accessibility\s+Level/g, "\n\n## Accessibility Level\n")
      .replace(/Summary/g, "\n\n## Summary\n")
      .replace(/Key\s+Risks/g, "\n\n## Key Risks\n")
      .replace(/Recommendations/g, "\n\n## Recommendations\n")
      .replace(/Estimated\s+Cost/g, "\n\n## Estimated Cost\n")
      .replace(/Priority\s+Actions/g, "\n\n## Priority Actions\n")

      .replace(/\s-\s(?=[A-Z])(?!\d)/g, "\n- ")
      .replace(/\n{3,}/g, "\n\n");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setHasSubmitted(true);
    setLoading(true);
    setStreamedReport("");

    try {
      const response = await fetch(
        "https://api.home-helper.com/api/accessibility/generate-accessibility-report",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ responses }),
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (let line of lines) {
          if (line.startsWith("data: ")) {
            let data = line.replace("data: ", "");

            if (data === "[DONE]") {
              setLoading(false);
              return;
            }

            setStreamedReport((prev) => {
              const combined = prev + data;
              return cleanChunk(combined);
            });
          }
        }
      }
    } catch (err) {
      console.error("Streaming error:", err);
      setLoading(false);
    }
  };

  const extractRiskLevel = (text) => {
    const match = text.match(/Accessibility Level\s*(.*?)(\n|$)/i);
    return match ? match[1].trim() : null;
  };

  const riskLevel = extractRiskLevel(streamedReport);

  const getRiskColor = (level) => {
    if (!level) return "#9ca3af";

    const l = level.toLowerCase();

    if (l.includes("high") || l.includes("significant")) return "#dc2626";
    if (l.includes("moderate")) return "#f59e0b";
    if (l.includes("low") || l.includes("good")) return "#16a34a";

    return "#6b7280";
  };

  if (!isHydrated) {
    return <div style={{ padding: 20 }}>Loading your report...</div>;
  }

  return (
    <div className="tool accessibility-planner-tool">
      <header className="tool-header">
        <h1 className="tool-title">{title}</h1>
        <p className="tool-description">{description}</p>
      </header>

      {!hasSubmitted && (
        <form style={{ marginTop: -42 }} onSubmit={handleSubmit}>
          {sections.map((section) => (
            <div key={section.id} className="tool-section">
              <strong>{section.title}</strong>

              {section.questions.map((question) => (
                <div className="tool-form-row" key={question.id}>
                  <label className="tool-label">{question.label}</label>

                  <div className="tool-form">
                    {question.options.map((option) => (
                      <label key={option.value}>
                        <input
                          type="radio"
                          name={question.id}
                          value={option.value}
                          checked={responses[question.id] === option.value}
                          onChange={() =>
                            handleChange(question.id, option.value)
                          }
                        />{" "}
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="tool-actions">
            <button type="submit" className="tool-button-primary">
              {loading ? "Generating..." : ctaLabel}
            </button>
          </div>
        </form>
      )}

      {hasSubmitted && (
        <section className="tool-results">
          <div className="tool-result-card">

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div>
                <strong>{result.title}</strong>
                <p className="tool-muted">{result.disclaimer}</p>
              </div>
            </div>

            {riskLevel && (
              <div style={{ marginTop: 10, marginBottom: 20 }}>
                <strong>Accessibility Risk Level:</strong>

                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: getRiskColor(riskLevel),
                    }}
                  />

                  <span style={{ fontWeight: 500 }}>
                    {riskLevel}
                  </span>
                </div>
              </div>
            )}

            {loading && <p>Generating your report...</p>}

            <div style={{ marginTop: 10 }}>
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  h2: (props) => (
                    <h2 style={{ marginTop: 20, fontSize: 18 }} {...props} />
                  ),
                  li: ({ children }) => {
                    let text = children?.toString?.() || "";

                    let matchedSlug = null;

                    Object.entries(recommendationLinks).forEach(([keyword, slug]) => {
                      const regex = new RegExp(`\\b(${keyword})\\b`, "i");
                      if (!matchedSlug && regex.test(text)) {
                        matchedSlug = slug;
                      }
                    });

                    if (matchedSlug) {
                      const url = `/in-town/${matchedSlug}`;

                      let icon = "🛠️";
                      let bgColor = "#eff6ff";

                      if (matchedSlug === "showers-walk-in-tubs") {
                        icon = "🚿";
                        bgColor = "#dbeafe"; // blue-100, more saturated
                      } else if (matchedSlug === "flooring") {
                        icon = "🧱";
                        bgColor = "#fef3c7"; // amber-100
                      } else if (matchedSlug === "exterior-door-replacement") {
                        icon = "🚪";
                        bgColor = "#dcfce7"; // green-100
                      }

                      return (
                        <div
                          onClick={() => (window.location.href = url)}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "16px 18px",
                            marginBottom: 10,
                            cursor: "pointer",
                            background: bgColor,
                            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              marginBottom: 4,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 24,
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              {icon}
                            </div>

                            <div style={{ fontWeight: 500 }}>{text}</div>
                          </div>

                          <div
                            style={{
                              fontSize: 13,
                              color: "#2563eb",
                              fontWeight: 500,
                              marginTop: 4,
                            }}
                          >
                            View service →
                          </div>
                        </div>
                      );
                    }

                    return <li style={{ marginBottom: 6 }}>{children}</li>;
                  },
                  p: (props) => (
                    <p style={{ lineHeight: 1.6 }} {...props} />
                  ),
                  strong: (props) => (
                    <strong style={{ fontWeight: 600 }} {...props} />
                  ),
                }}
              >
                {
                  streamedReport.replace(
                    /##\s*Accessibility Level[\s\S]*?(?=\n##|\n\n##|$)/i,
                    ""
                  )
                }
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

AccessibilityPlannerTool.propTypes = {
  category: PropTypes.string,
};

export default AccessibilityPlannerTool;